import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PRODUCT_VISIBILITY_OPTIONS } from "../constants/productVisibility";

export default function ProductVisibilityChip({
  item,
  canChangeVisibility,
  onChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const option =
    PRODUCT_VISIBILITY_OPTIONS.find(({ value }) => value === item.visibility) ||
    PRODUCT_VISIBILITY_OPTIONS[0];

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsidePointerDown = (event) => {
      if (
        !pickerRef.current?.contains(event.target) &&
        !dropdownRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }
    const updatePosition = () => {
      const bounds = buttonRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const width = 256;
      setPosition({
        left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)),
        top: bounds.bottom + 4,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  return (
    <div
      ref={pickerRef}
      className={`relative flex flex-col items-start gap-1 ${
        isOpen ? "z-30" : ""
      }`}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Visibility: ${option.label} for ${item.model}${
          canChangeVisibility ? ". Edit visibility" : ""
        }`}
        aria-haspopup={canChangeVisibility ? "true" : undefined}
        aria-expanded={isOpen}
        onClick={() => canChangeVisibility && setIsOpen((open) => !open)}
        disabled={!canChangeVisibility}
        title={option.description}
        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
          item.visibility === "PUBLISHED"
            ? "bg-green-100 text-green-800"
            : "bg-amber-100 text-amber-900"
        } ${
          canChangeVisibility
            ? "cursor-pointer hover:ring-2 hover:ring-blue-300"
            : "cursor-default"
        }`}
      >
        {option.label}
      </button>
      {isOpen && canChangeVisibility && position
        ? createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: "fixed",
                left: position.left,
                top: position.top,
                maxHeight: "calc(100vh - 16px)",
                overflowY: "auto",
              }}
              className="z-[1000] w-64 space-y-1 rounded-md border border-zinc-300 bg-white p-2 shadow-lg"
              role="group"
              aria-label={`Choose visibility for ${item.model}`}
            >
              {PRODUCT_VISIBILITY_OPTIONS.map(
                ({ value, label, description }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      onChange(item, value);
                      setIsOpen(false);
                    }}
                    disabled={item.visibility === value}
                    className="block w-full rounded px-2 py-1 text-left hover:bg-zinc-100 disabled:bg-blue-50 disabled:font-semibold"
                  >
                    <span className="block text-xs text-zinc-900">{label}</span>
                    <span className="block whitespace-normal text-[10px] font-normal text-zinc-600">
                      {description}
                    </span>
                  </button>
                ),
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
